import { useEffect, useMemo, useRef, useState } from "react";
import {
  Check,
  Eye,
  Pencil,
  Plus,
  Search,
  Settings,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SHX } from "@/constants/testIds";
import { getPxConcept, listPxMaster } from "@/lib/pxMasterApi";
import {
  createSurgicalHistory,
  listSurgicalHistory,
  removeSurgicalHistory,
  updateSurgicalHistory,
} from "@/lib/surgicalHistoryApi";
import {
  SHX_LATERALITY,
  SHX_SORT,
  SHX_VALUE_SETS,
  formatReviewedAt,
  formatSearchPx,
  hasBodySiteField,
  isoToManualDate,
  lateralityLabel,
  manualDateToIso,
  sourceLabel,
  standardMappings,
  toSurgicalHistoryPayload,
} from "@/lib/surgicalHistoryCatalog";

function apiErrorMessage(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((row) => row.msg).join("; ");
  return "Unable to update Surgical History.";
}

function emptyForm() {
  return {
    selected: null,
    performedDate: "",
    laterality: "na",
    performerDisplay: "",
    locationDisplay: "",
    comment: "",
    reviewed: false,
  };
}

function FieldLabel({ children }) {
  return <label className="emr-shx__label mb-1 block text-xs font-medium text-slate-600">{children}</label>;
}

function selectClassName() {
  return "h-9 w-full rounded-md border border-[#e4d8c8] bg-white px-2 text-sm text-slate-800";
}

export default function SurgicalHistoryWorkspace({
  patientId,
  patient,
  currentUser,
  dateOfService,
  onClose,
  onPrevious,
  onNext,
  onChange,
  accessReadOnly = false,
}) {
  const searchRef = useRef(null);
  const [items, setItems] = useState([]);
  const [quickEntry, setQuickEntry] = useState([]);
  const [valueSets, setValueSets] = useState(SHX_VALUE_SETS);
  const [sortOptions, setSortOptions] = useState(SHX_SORT);
  const [lateralityOptions, setLateralityOptions] = useState(SHX_LATERALITY);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [category, setCategory] = useState("surgical-history");
  const [sortBy, setSortBy] = useState("relevance");
  const [form, setForm] = useState(() => emptyForm());
  const [mode, setMode] = useState("add");
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showAllMappings, setShowAllMappings] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const readOnly = mode === "view" || accessReadOnly;

  useEffect(() => {
    const handle = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(handle);
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    listSurgicalHistory(patientId)
      .then((data) => {
        if (cancelled) return;
        setItems(data.items || []);
        const catalogs = data.catalogs || {};
        setQuickEntry(catalogs.quickEntry || []);
        if (catalogs.valueSets?.length) {
          setValueSets([{ id: "", label: "All Procedures" }, ...catalogs.valueSets]);
        }
        if (catalogs.sortOptions?.length) setSortOptions(catalogs.sortOptions);
        if (catalogs.laterality?.length) setLateralityOptions(catalogs.laterality);
        onChange?.({ items: data.items || [], review: data.review || null });
      })
      .catch((error) => {
        if (cancelled) return;
        setItems([]);
        setQuickEntry([]);
        setLoadError(apiErrorMessage(error));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [patientId]);

  useEffect(() => {
    if (!debounced) {
      setResults([]);
      setSearching(false);
      return undefined;
    }
    let cancelled = false;
    setSearching(true);
    listPxMaster({
      q: debounced,
      active: true,
      context: category || undefined,
      sort: sortBy,
    })
      .then((data) => {
        if (cancelled) return;
        setResults(data.items || []);
      })
      .catch(() => {
        if (!cancelled) setResults([]);
      })
      .finally(() => {
        if (!cancelled) setSearching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debounced, category, sortBy]);

  const mappings = useMemo(() => standardMappings(form.selected), [form.selected]);
  const visibleMappings = showAllMappings ? mappings : mappings.slice(0, 3);
  const reviewerName =
    (editingId && items.find((row) => row.id === editingId)?.reviewedByDisplay) ||
    currentUser?.displayName ||
    currentUser?.email ||
    "—";
  const reviewWhen = editingId
    ? formatReviewedAt(items.find((row) => row.id === editingId)?.reviewedAt) || formatReviewedAt(new Date().toISOString())
    : formatReviewedAt(new Date().toISOString());

  function resetForm() {
    setForm(emptyForm());
    setMode("add");
    setEditingId(null);
    setQuery("");
    setResults([]);
    setShowAllMappings(false);
  }

  async function selectConcept(item) {
    if (readOnly || saving) return;
    const conceptId = item?.pxConceptId || item?.id;
    if (!conceptId) return;
    try {
      const full = item?.codeableConcept || item?.mappings ? item : await getPxConcept(conceptId);
      setForm((current) => ({ ...current, selected: full }));
      setQuery("");
      setResults([]);
      setShowAllMappings(false);
    } catch (error) {
      toast.error(apiErrorMessage(error));
    }
  }

  function loadItem(item, nextMode) {
    setMode(accessReadOnly ? "view" : nextMode);
    setEditingId(item.id);
    setForm({
      selected: item,
      performedDate: item.performedDate || "",
      laterality: item.lateralityCode || "na",
      performerDisplay: item.performerDisplay || "",
      locationDisplay: item.locationDisplay || "",
      comment: item.comment || "",
      reviewed: !!item.reviewed,
    });
    setQuery("");
    setResults([]);
    setShowAllMappings(false);
  }

  function payloadFromForm() {
    return toSurgicalHistoryPayload(form.selected, {
      performedDate: form.performedDate || null,
      laterality: form.laterality,
      performerDisplay: form.performerDisplay.trim() || null,
      locationDisplay: form.locationDisplay.trim() || null,
      comment: form.comment.trim() || null,
      reviewed: form.reviewed,
    });
  }

  async function save({ addAnother }) {
    if (!form.selected || saving || readOnly) return;
    setSaving(true);
    try {
      const payload = payloadFromForm();
      let saved;
      if (editingId && mode === "edit") {
        saved = await updateSurgicalHistory(editingId, payload);
        setItems((current) => current.map((row) => (row.id === saved.id ? saved : row)));
        toast.success("Surgical history updated");
      } else {
        saved = await createSurgicalHistory(patientId, payload);
        setItems((current) => [saved, ...current.filter((row) => row.id !== saved.id)]);
        toast.success("Added to Surgical History");
      }
      const nextItems = editingId && mode === "edit"
        ? items.map((row) => (row.id === saved.id ? saved : row))
        : [saved, ...items.filter((row) => row.id !== saved.id)];
      onChange?.({ items: nextItems });
      if (addAnother) {
        resetForm();
      } else {
        onClose?.();
      }
    } catch (error) {
      toast.error(apiErrorMessage(error));
    } finally {
      setSaving(false);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    try {
      await removeSurgicalHistory(deleteTarget.id);
      const next = items.filter((row) => row.id !== deleteTarget.id);
      setItems(next);
      onChange?.({ items: next });
      if (editingId === deleteTarget.id) resetForm();
      toast.success("Surgical history item removed");
    } catch (error) {
      toast.error(apiErrorMessage(error));
    } finally {
      setDeleteTarget(null);
    }
  }

  const selected = form.selected;
  const selectedActive = selected?.active !== false;

  return (
    <div
      data-testid={SHX.panel}
      className="emr-shx overflow-hidden rounded-lg border border-[#e4d8c8] bg-white shadow-[0_1px_2px_rgba(50,32,12,0.06)]"
    >
      <div className="flex items-center justify-between border-b border-[#e4d8c8] px-5 py-3">
        <div className="flex items-center gap-2">
          <Pencil className="h-4 w-4 text-[#6B4C9A]" />
          <h2 data-testid={SHX.title} className="text-base font-semibold text-[#6B4C9A]">
            {mode === "edit" ? "Edit Surgical History" : "Add Surgical History"}
          </h2>
        </div>
        <button
          type="button"
          data-testid={SHX.close}
          onClick={onClose}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="emr-shx__editor grid gap-4 px-5 py-4 xl:grid-cols-[minmax(0,1fr)_300px]">
        <section className="min-w-0 space-y-3">
          <h3 className="text-sm font-semibold text-[#6B4C9A]">Procedure Details</h3>
          {!selected && (
            <>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  ref={searchRef}
                  type="search"
                  autoComplete="off"
                  spellCheck={false}
                  data-testid={SHX.search}
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search by procedure name or Px Code..."
                  className="h-10 bg-white pl-10"
                  disabled={readOnly}
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <FieldLabel>Category / Value Set</FieldLabel>
                  <select
                    data-testid={SHX.category}
                    className={selectClassName()}
                    value={category}
                    onChange={(event) => setCategory(event.target.value)}
                    disabled={readOnly}
                  >
                    {valueSets.map((option) => (
                      <option key={option.id || "all"} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <FieldLabel>Sort by</FieldLabel>
                  <select
                    data-testid={SHX.sort}
                    className={selectClassName()}
                    value={sortBy}
                    onChange={(event) => setSortBy(event.target.value)}
                    disabled={readOnly}
                  >
                    {sortOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {(searching || results.length > 0) && (
                <ul
                  data-testid={SHX.results}
                  className="max-h-48 overflow-y-auto rounded-md border border-[#e4d8c8] bg-white"
                >
                  {searching && <li className="px-3 py-2 text-sm text-slate-500">Searching Px Master…</li>}
                  {results.map((item) => (
                    <li key={item.id}>
                      <button
                        type="button"
                        data-testid={`${SHX.result}-${item.localCode}`}
                        onClick={() => selectConcept(item)}
                        className="block w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-[#F3EEF8] hover:text-[#6B4C9A]"
                      >
                        {formatSearchPx(item)}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}

          {selected && (
            <div data-testid={SHX.selected} className="rounded-md border border-[#e4d8c8] bg-[#FBF8FF] p-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-metadata font-semibold uppercase tracking-wide text-slate-500">
                    Selected Procedure
                  </p>
                  <p className="mt-0.5 text-base font-semibold text-slate-900">
                    {selected.display || selected.shortDisplay}
                  </p>
                  <p data-testid={SHX.pxCode} className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-600">
                    <span>Px Code (Local): {selected.localCode}</span>
                    <span
                      className={`rounded-full px-2 py-0.5 text-metadata font-semibold ${
                        selectedActive ? "bg-[#EDE6F7] text-[#6B4C9A]" : "bg-slate-200 text-slate-600"
                      }`}
                    >
                      {selectedActive ? "Active" : "Inactive"}
                    </span>
                  </p>
                </div>
                {!readOnly && (
                  <Button
                    type="button"
                    variant="outline"
                    data-testid={SHX.changeProcedure}
                    onClick={() => setForm((current) => ({ ...current, selected: null }))}
                    className="h-8 border-[#6B4C9A]/40 text-[#6B4C9A]"
                  >
                    Change Procedure
                  </Button>
                )}
              </div>
              <div data-testid={SHX.mappings} className="mt-3">
                <p className="text-metadata font-semibold uppercase tracking-wide text-slate-500">
                  Code System Mappings
                </p>
                {visibleMappings.length ? (
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {visibleMappings.map((row) => (
                      <span
                        key={`${row.system}-${row.code}`}
                        className="rounded border border-[#d4c8e8] bg-white px-2 py-0.5 text-metadata text-[#6B4C9A]"
                      >
                        {row.label}: {row.code}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="mt-1 text-xs text-slate-500">
                    No standard terminology mappings on this Px Master concept.
                  </p>
                )}
                {mappings.length > 3 && (
                  <button
                    type="button"
                    data-testid={SHX.viewMappings}
                    onClick={() => setShowAllMappings((value) => !value)}
                    className="mt-1 text-xs font-medium text-[#6B4C9A] hover:underline"
                  >
                    {showAllMappings ? "Hide mappings" : "View all mappings"}
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <FieldLabel>Date Performed</FieldLabel>
              <Input
                type="date"
                data-testid={SHX.datePerformed}
                value={form.performedDate}
                disabled={readOnly}
                onChange={(event) =>
                  setForm((current) => ({ ...current, performedDate: event.target.value }))
                }
                className="h-9 bg-white"
              />
            </div>
            <div>
              <FieldLabel>Manual date entry</FieldLabel>
              <Input
                data-testid={SHX.dateManual}
                value={isoToManualDate(form.performedDate)}
                disabled={readOnly}
                placeholder="MM/DD/YYYY"
                onChange={(event) => {
                  const iso = manualDateToIso(event.target.value);
                  if (iso) setForm((current) => ({ ...current, performedDate: iso }));
                }}
                className="h-9 bg-white"
              />
            </div>
          </div>

          <div>
            <FieldLabel>Laterality</FieldLabel>
            <select
              data-testid={SHX.laterality}
              className={selectClassName()}
              value={form.laterality}
              disabled={readOnly}
              onChange={(event) => setForm((current) => ({ ...current, laterality: event.target.value }))}
            >
              {lateralityOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <FieldLabel>Surgeon / Provider</FieldLabel>
            <Input
              data-testid={SHX.performer}
              value={form.performerDisplay}
              disabled={readOnly}
              onChange={(event) =>
                setForm((current) => ({ ...current, performerDisplay: event.target.value }))
              }
              className="h-9 bg-white"
            />
          </div>

          <div>
            <FieldLabel>Facility / Location</FieldLabel>
            <Input
              data-testid={SHX.location}
              value={form.locationDisplay}
              disabled={readOnly}
              onChange={(event) =>
                setForm((current) => ({ ...current, locationDisplay: event.target.value }))
              }
              className="h-9 bg-white"
            />
          </div>

          <div>
            <FieldLabel>Comments / Details</FieldLabel>
            <textarea
              data-testid={SHX.comment}
              value={form.comment}
              disabled={readOnly}
              rows={3}
              onChange={(event) => setForm((current) => ({ ...current, comment: event.target.value }))}
              className="w-full rounded-md border border-[#e4d8c8] bg-white px-3 py-2 text-sm"
            />
          </div>

          <div className="rounded-md border border-[#e4d8c8] bg-[#FBF8FF] px-3 py-2">
            <label className="flex items-start gap-2 text-sm text-slate-800">
              <input
                type="checkbox"
                data-testid={SHX.review}
                checked={form.reviewed}
                disabled={readOnly}
                onChange={(event) => setForm((current) => ({ ...current, reviewed: event.target.checked }))}
                className="mt-0.5 h-4 w-4 accent-[#6B4C9A]"
              />
              <span>
                <span className="font-medium">Mark as reviewed</span>
                {form.reviewed && (
                  <span data-testid={SHX.reviewedBy} className="mt-0.5 block text-xs text-slate-500">
                    Reviewed by: {reviewerName} Date: {reviewWhen}
                  </span>
                )}
              </span>
            </label>
            <p data-testid={SHX.lastReviewed} className="sr-only">
              {form.reviewed ? `Reviewed by ${reviewerName}` : "Not yet reviewed"}
            </p>
          </div>
        </section>

        <aside data-testid={SHX.quickEntry} className="emr-shx__quick rounded-md border border-[#e4d8c8] bg-white p-3">
          <div className="mb-3 flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-[#6B4C9A]">Quick Entry</h3>
            <a
              href="/settings/px-master"
              data-testid={SHX.manageQuickEntry}
              className="inline-flex items-center gap-1 text-xs font-medium text-[#6B4C9A] hover:underline"
            >
              <Settings className="h-3.5 w-3.5" />
              Manage Quick Entry
            </a>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {quickEntry.map((item) => (
              <button
                key={item.pxConceptId || item.id}
                type="button"
                data-testid={`${SHX.quickChip}-${item.localCode}`}
                onClick={() => selectConcept(item)}
                disabled={readOnly || saving}
                className="inline-flex items-center gap-1.5 rounded-md border border-[#e4d8c8] bg-[#F8F6FC] px-2 py-1.5 text-left text-xs font-medium text-slate-700 hover:border-[#6B4C9A]/40 hover:bg-[#F3EEF8] hover:text-[#6B4C9A]"
              >
                <Plus className="h-3.5 w-3.5 shrink-0 text-[#6B4C9A]" />
                <span className="min-w-0 leading-snug">{item.chip || item.shortDisplay || item.display}</span>
              </button>
            ))}
          </div>
          {!quickEntry.length && (
            <p className="text-sm text-slate-400">Quick Entry is configured in Settings → Px Master.</p>
          )}
          <Button
            type="button"
            variant="outline"
            data-testid={SHX.searchAll}
            onClick={() => {
              setCategory("");
              setForm((current) => ({ ...current, selected: null }));
              setTimeout(() => searchRef.current?.focus(), 0);
            }}
            className="mt-4 h-10 w-full border-[#6B4C9A] text-[#6B4C9A] hover:bg-[#F3EEF8]"
          >
            Search All Procedures
          </Button>
        </aside>
      </div>

      <section className="border-t border-[#e4d8c8] px-5 py-4">
        <h3 className="mb-3 text-sm font-semibold text-[#6B4C9A]">Past Surgical History</h3>
        {loadError && <p className="mb-3 text-sm text-amber-700">{loadError}</p>}
        <div className="overflow-x-auto rounded-md border border-[#e4d8c8]">
          <table data-testid={SHX.table} className="w-full min-w-[920px] text-sm">
            <thead className="bg-[#F6F2FB] text-left text-table-head font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2">Procedure</th>
                <th className="px-3 py-2">Laterality</th>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Surgeon / Provider</th>
                <th className="px-3 py-2">Facility / Location</th>
                <th className="px-3 py-2">Source</th>
                <th className="px-3 py-2">Reviewed</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-3 py-6 text-center text-slate-400">
                    No surgical history recorded for this patient.
                  </td>
                </tr>
              )}
              {items.map((item) => (
                <tr
                  key={item.id}
                  data-testid={`${SHX.row}-${item.localCode}`}
                  className="border-t border-[#f0e6d8]"
                >
                  <td className="px-3 py-2 font-medium text-[#6B4C9A]">{item.display}</td>
                  <td className="px-3 py-2 text-slate-600">
                    {lateralityLabel(item.lateralityCode, item.laterality)}
                  </td>
                  <td className="px-3 py-2 text-slate-700">
                    {item.performedDate ? isoToManualDate(item.performedDate) : "—"}
                  </td>
                  <td className="px-3 py-2 text-slate-700">{item.performerDisplay || "—"}</td>
                  <td className="px-3 py-2 text-slate-700">{item.locationDisplay || "—"}</td>
                  <td className="px-3 py-2 text-slate-600">{sourceLabel(item.source)}</td>
                  <td className="px-3 py-2">
                    {item.reviewed ? (
                      <Check className="h-4 w-4 text-emerald-600" aria-label="Reviewed" />
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-1">
                      <button
                        type="button"
                        data-testid={`${SHX.view}-${item.localCode}`}
                        onClick={() => loadItem(item, "view")}
                        className="inline-flex h-7 w-7 items-center justify-center rounded text-slate-500 hover:bg-[#F3EEF8] hover:text-[#6B4C9A]"
                        aria-label={`View ${item.display}`}
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </button>
                      {accessReadOnly ? null : (
                      <button
                        type="button"
                        data-testid={`${SHX.edit}-${item.localCode}`}
                        onClick={() => loadItem(item, "edit")}
                        className="inline-flex h-7 w-7 items-center justify-center rounded text-slate-500 hover:bg-[#F3EEF8] hover:text-[#6B4C9A]"
                        aria-label={`Edit ${item.display}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      )}
                      {accessReadOnly ? null : (
                      <button
                        type="button"
                        data-testid={`${SHX.delete}-${item.localCode}`}
                        onClick={() => setDeleteTarget(item)}
                        className="inline-flex h-7 w-7 items-center justify-center rounded text-slate-500 hover:bg-rose-50 hover:text-rose-600"
                        aria-label={`Delete ${item.display}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[#e4d8c8] bg-[#FBF8FF] px-5 py-3">
        <Button
          type="button"
          variant="outline"
          data-testid="patient-chart-shx-cancel"
          onClick={onClose}
          className="border-[#e4d8c8]"
        >
          Cancel
        </Button>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            data-testid={SHX.saveAnother}
            disabled={!selected || saving || readOnly}
            onClick={() => save({ addAnother: true })}
            className="border-[#6B4C9A] text-[#6B4C9A] hover:bg-[#F3EEF8]"
          >
            Save & Add Another
          </Button>
          <Button
            type="button"
            data-testid={SHX.saveClose}
            disabled={!selected || saving || readOnly}
            onClick={() => save({ addAnother: false })}
            className="bg-[#6B4C9A] text-white hover:bg-[#5A3D86]"
          >
            Save & Close
          </Button>
        </div>
      </div>

      {!hasBodySiteField() && <span data-testid="patient-chart-shx-no-body-site" className="hidden" />}

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete surgical history?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes <span className="font-medium">{deleteTarget?.display}</span> from this
              patient&apos;s surgical history. The Px Master concept is not deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-rose-600 hover:bg-rose-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
